import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Shield, Users, Store, Clock, BadgeCheck, MessageSquare, Star, LayoutGrid, MapPin, LogOut, Loader2, CalendarCheck, IndianRupee, Percent, Undo2, LayoutDashboard, CreditCard, Tags, SlidersHorizontal, Megaphone, TicketPercent, FileText, Bell, Settings, PlugZap, ScrollText, PanelLeftClose, PanelLeftOpen, Search, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { BRAND_LOGO, formatINR } from "@/lib/constants";
import VendorsTab from "./VendorsTab";
import EmployeesTab from "./EmployeesTab";
import FieldBuilderTab from "./FieldBuilderTab";
import LocationsTab from "./LocationsTab";
import CategoryManagerTab from "./CategoryManagerTab";
import { BannersTab, CustomersTab, BookingsTab, PaymentsTab, BillingTab, CouponsTab, ReviewsTab, PlansTab, PagesTab, SettingsTab, PaymentSettingsTab, IntegrationsTab, NotifyTab, AuditTab, HomepageManagerTab } from "./tabs";
import AuthDialog from "@/components/AuthDialog";

const NAV_GROUPS = [
  {
    label: "MAIN",
    items: [
      ["dashboard", "Dashboard", LayoutDashboard],
        ["kyc", "KYC Queue", Shield],
      ["vendors", "Vendors", Store],
      ["customers", "Customers", Users],
      ["employees", "Employees", UserPlus],
    ],
  },
  {
    label: "OPERATIONS",
    items: [
      ["bookings", "Bookings", CalendarCheck],
      ["payments", "Payments & Payouts", CreditCard],
    ["billing", "Billing & Invoices", IndianRupee],
      ["paysettings", "Payment Settings", IndianRupee],
    ],
  },
  {
    label: "CATALOG",
    items: [
      ["categories", "Categories", Tags],
      ["fields", "Field Builder", SlidersHorizontal],
      ["locations", "Locations", MapPin],
      ["plans", "Vendor Plans", BadgeCheck],
    ],
  },
  {
    label: "MARKETING",
    items: [
      ["reviews", "Reviews", Star],
      ["banners", "Banners & Ads", Megaphone],
      ["coupons", "Coupons & Offers", TicketPercent],
    ],
  },
  {
    label: "CONTENT & COMMUNICATION",
    items: [
      ["homepage", "Homepage Manager", Home],
      ["pages", "CMS Pages", FileText],
      ["notify", "Notifications", Bell],
    ],
  },
  {
    label: "SYSTEM",
    items: [
      ["integrations", "Integrations", PlugZap],
      ["settings", "Settings", Settings],
      ["audit", "Audit Logs", ScrollText],
    ],
  },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout, setAuthOpen } = useAuth();
  const [stats, setStats] = useState(null); const [activeTab, setActiveTab] = useState("dashboard"); const [vendors, setVendors] = useState([]); const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true); const [err, setErr] = useState(false); const [allCats, setAllCats] = useState([]);
  const isAdmin = user && ["admin", "super_admin", "content_manager", "support_manager", "finance_manager"].includes(user.role);

  const loadAll = () => Promise.all([api.get("/admin/stats"), api.get("/admin/vendors"), api.get("/categories?include_all=true&include_inactive=true")])
    .then(([s, v, c]) => { setStats(s.data); setVendors(v.data); setCategories(c.data.filter((x) => !x.is_all && x.active)); setAllCats(c.data); }).catch(() => setErr(true)).finally(() => setLoading(false));
  useEffect(() => { if (authLoading) return; if (!user) { setAuthOpen(true); setLoading(false); return; } if (!isAdmin) { setErr(true); setLoading(false); return; } loadAll(); }, [authLoading, user]); // eslint-disable-line

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!user || err) return (
    <div className="min-h-screen flex items-center justify-center px-4 text-center"><AuthDialog /><div>
      <Shield className="h-12 w-12 mx-auto text-purple-500 mb-4" /><h2 className="font-display font-bold text-xl">Admin access required</h2>
      <div className="flex gap-2 justify-center mt-5"><Button onClick={() => setAuthOpen(true)} className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500">Login</Button><Button variant="outline" onClick={() => navigate("/")} className="rounded-xl">Marketplace</Button></div>
    </div></div>);

  if (!stats) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const pending = vendors.filter((v) => ["submitted", "under_review", "corrections_requested"].includes(v.status)).length;
  const cards = [
    { icon: Users, label: "Customers", value: stats.total_customers, color: "from-blue-500 to-indigo-500" },
    { icon: Store, label: "Vendors", value: stats.total_vendors, color: "from-purple-500 to-fuchsia-500" },
    { icon: Clock, label: "Pending KYC", value: stats.pending_vendors, color: "from-amber-500 to-orange-500" },
    { icon: BadgeCheck, label: "Verified", value: stats.verified_vendors, color: "from-emerald-500 to-teal-500" },
    { icon: MessageSquare, label: "Enquiries", value: stats.total_enquiries, color: "from-pink-500 to-rose-500" },
    { icon: CalendarCheck, label: "Bookings", value: stats.total_bookings, color: "from-teal-500 to-cyan-500" },
    { icon: IndianRupee, label: "GMV", value: formatINR(stats.gmv), color: "from-green-500 to-emerald-600" },
    { icon: Percent, label: "Commission", value: formatINR(stats.commission_earned), color: "from-violet-500 to-purple-600" },
    { icon: Undo2, label: "Refund requests", value: stats.refund_requests, color: "from-orange-500 to-red-500" },
    { icon: Star, label: "Pending reviews", value: stats.pending_reviews, color: "from-yellow-500 to-amber-500" },
    { icon: LayoutGrid, label: "Categories / Fields", value: `${stats.categories} / ${stats.fields}`, color: "from-slate-600 to-slate-800" },
    { icon: MapPin, label: "Cities", value: stats.cities, color: "from-cyan-600 to-blue-600" },
  ];

  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      style={{
        background:
          "radial-gradient(circle at 8% 8%, rgba(109,53,242,0.07), transparent 28%), radial-gradient(circle at 92% 12%, rgba(49,85,231,0.06), transparent 30%), radial-gradient(circle at 55% 100%, rgba(22,199,238,0.045), transparent 32%), #f7f8fc"
      }}
    >
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/95 backdrop-blur-xl">
      <div className="h-[72px] px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">

        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex items-center gap-3 min-w-0 group"
            title="Go to Marketplace"
          >
            <img
              src={BRAND_LOGO}
              className="h-10 w-10 rounded-xl shadow-sm ring-1 ring-slate-200"
              alt="MakeMyEventPro"
            />

            <div className="hidden sm:block text-left">
              <div className="font-display font-extrabold text-[15px] leading-tight text-slate-900">
                MakeMyEventPro
              </div>
              <div className="text-[11px] font-medium text-slate-500 mt-0.5">
                Super Admin Console
              </div>
            </div>
          </button>
        </div>

        <div className="hidden md:flex flex-1 max-w-xl mx-4 lg:mx-10">
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />

            <input
              type="search"
              placeholder="Search vendors, customers, bookings..."
              className="w-full h-11 rounded-xl border border-slate-200 bg-slate-50/80 pl-11 pr-4 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-purple-400 focus:bg-white focus:ring-4 focus:ring-purple-500/10"
            />

            <span className="hidden lg:block absolute right-3 top-1/2 -translate-y-1/2 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-400">
              SEARCH
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">

          <button
            type="button"
            className="relative h-10 w-10 rounded-xl border border-slate-200 bg-white flex items-center justify-center text-slate-600 transition-all hover:bg-slate-50 hover:border-slate-300"
            title="Notifications"
          >
            <Bell className="h-4 w-4" />
            <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-purple-600 ring-2 ring-white" />
          </button>

          <div className="hidden sm:block h-8 w-px bg-slate-200 mx-1" />

          <button
            type="button"
            className="flex items-center gap-3 rounded-xl px-2 py-1.5 transition-all hover:bg-slate-50"
            title="Super Admin Profile"
          >
            <img
              src="/admin-profile.jpg"
              alt="Super Admin"
              className="h-10 w-10 rounded-full object-cover ring-2 ring-white shadow-md"
            />

            <div className="hidden lg:block text-left leading-tight">
              <div className="text-sm font-bold text-slate-900">
                {user.name || "Super Admin"}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {user.email || "Administrator"}
              </div>
            </div>
          </button>

          <Button
            variant="outline"
            size="sm"
            className="h-10 w-10 rounded-xl border-slate-200 p-0 hover:bg-slate-50"
            onClick={logout}
            title="Logout"
          >
            <LogOut className="h-4 w-4 text-slate-600" />
          </Button>

        </div>
      </div>
    </header>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
  <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-6 items-start">
    <aside className="lg:sticky lg:top-24 rounded-2xl border border-border bg-card p-3 shadow-sm">
      <div className="px-3 pt-2 pb-4">
        <div className="text-sm font-bold">Admin Workspace</div>
        <div className="text-xs text-muted-foreground mt-1">
          Manage your marketplace
        </div>
      </div>
          <TabsList className="w-full h-auto flex-col items-stretch justify-start bg-transparent p-0 gap-1">
  {NAV_GROUPS.map((group) => (
    <div key={group.label} className="mb-4">
      <div className="px-3 pb-2 text-[10px] font-bold tracking-[0.16em] text-muted-foreground">
        {group.label}
      </div>

      <div className="space-y-1">
        {group.items.map(([k, label, Icon]) => (
          <TabsTrigger
            key={k}
            value={k}
            data-testid={`admin-tab-${k}`}
            className="w-full justify-start gap-3 rounded-xl px-3 py-2.5 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm"
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{label}</span>
            {k === "kyc" && pending > 0 ? (
              <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                {pending}
              </span>
            ) : null}
          </TabsTrigger>
        ))}
      </div>
    </div>
  ))}
</TabsList>
    </aside>

    <main className="min-w-0">
      <div className="mb-6">
<h1 className="font-display font-extrabold text-2xl sm:text-3xl">Platform Overview</h1><p className="text-muted-foreground">MakeMyEventPro · full marketplace control</p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-6">{cards.map((c) => <div key={c.label} className="rounded-2xl border border-border bg-card p-4"><div className={`h-8 w-8 rounded-xl bg-gradient-to-br ${c.color} flex items-center justify-center mb-2`}><c.icon className="h-4 w-4 text-white" /></div><div className="text-xs text-muted-foreground">{c.label}</div><div className="font-display font-extrabold text-xl">{c.value}</div></div>)}</div>
      </div>

      <TabsContent value="dashboard" className="pt-5">
  <div className="space-y-6">

    <div className="rounded-3xl border border-white/70 bg-white/80 backdrop-blur-xl shadow-sm p-5 sm:p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-primary">
            Command Center
          </div>
          <h2 className="mt-2 text-2xl sm:text-3xl font-display font-extrabold tracking-tight">
            Welcome back, {user?.name || "Super Admin"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Monitor your marketplace, vendors, customers and transactions from one place.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:flex">
          <button type="button" onClick={() => setActiveTab("vendors")} className="rounded-xl border bg-white px-4 py-2.5 text-sm font-semibold shadow-sm">
            Manage Vendors
          </button>

          <button type="button" onClick={() => setActiveTab("kyc")} className="rounded-xl border bg-white px-4 py-2.5 text-sm font-semibold shadow-sm">
            Review KYC
          </button>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">

      <div className="xl:col-span-2 rounded-3xl border border-white/70 bg-white/85 backdrop-blur-xl shadow-sm p-5 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-display font-bold text-lg">Marketplace Snapshot</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Current platform activity
            </p>
          </div>

          <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center">
            <LayoutGrid className="h-4 w-4 text-primary" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">

          <div className="rounded-2xl bg-blue-50/80 border border-blue-100 p-4">
            <Users className="h-5 w-5 text-blue-600 mb-3" />
            <div className="text-xs text-muted-foreground">Customers</div>
            <div className="text-2xl font-display font-extrabold mt-1">
              {stats.total_customers}
            </div>
          </div>

          <div className="rounded-2xl bg-purple-50/80 border border-purple-100 p-4">
            <Store className="h-5 w-5 text-purple-600 mb-3" />
            <div className="text-xs text-muted-foreground">Vendors</div>
            <div className="text-2xl font-display font-extrabold mt-1">
              {stats.total_vendors}
            </div>
          </div>

          <div className="rounded-2xl bg-pink-50/80 border border-pink-100 p-4">
            <MessageSquare className="h-5 w-5 text-pink-600 mb-3" />
            <div className="text-xs text-muted-foreground">Enquiries</div>
            <div className="text-2xl font-display font-extrabold mt-1">
              {stats.total_enquiries}
            </div>
          </div>

          <div className="rounded-2xl bg-cyan-50/80 border border-cyan-100 p-4">
            <CalendarCheck className="h-5 w-5 text-cyan-600 mb-3" />
            <div className="text-xs text-muted-foreground">Bookings</div>
            <div className="text-2xl font-display font-extrabold mt-1">
              {stats.total_bookings}
            </div>
          </div>

        </div>

        <div className="mt-5 rounded-2xl border bg-slate-50/80 p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold">Vendor verification</div>
              <div className="text-xs text-muted-foreground">
                Verified vs pending onboarding
              </div>
            </div>

            <span className="text-sm font-bold text-emerald-600">
              {stats.verified_vendors} verified
            </span>
          </div>

          <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500"
              style={{
                width: `${stats.total_vendors > 0
                  ? Math.min(100, (stats.verified_vendors / stats.total_vendors) * 100)
                  : 0}%`
              }}
            />
          </div>

          <div className="flex justify-between mt-2 text-[11px] text-muted-foreground">
            <span>{stats.pending_vendors} pending</span>
            <span>{stats.total_vendors} total vendors</span>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-white/70 bg-slate-950 text-white shadow-sm p-5 sm:p-6 overflow-hidden relative">

        <div className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-purple-500/20 blur-2xl" />
        <div className="absolute -left-12 -bottom-12 h-36 w-36 rounded-full bg-cyan-400/10 blur-2xl" />

        <div className="relative">

          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.16em] text-slate-400">
                Marketplace Value
              </div>
              <h3 className="mt-2 text-lg font-display font-bold">
                GMV Overview
              </h3>
            </div>

            <IndianRupee className="h-5 w-5 text-emerald-400" />
          </div>

          <div className="mt-8">
            <div className="text-3xl sm:text-4xl font-display font-extrabold">
              {formatINR(stats.gmv)}
            </div>

            <div className="text-xs text-slate-400 mt-1">
              Gross merchandise value
            </div>
          </div>

          <div className="mt-7 grid grid-cols-2 gap-3">

            <div className="rounded-2xl bg-white/10 border border-white/10 p-3">
              <div className="text-xs text-slate-400">Commission</div>
              <div className="font-bold mt-1">
                {formatINR(stats.commission_earned)}
              </div>
            </div>

            <div className="rounded-2xl bg-white/10 border border-white/10 p-3">
              <div className="text-xs text-slate-400">Bookings</div>
              <div className="font-bold mt-1">
                {stats.total_bookings}
              </div>
            </div>

          </div>

          <div className="mt-6 text-[11px] text-slate-400">
            Revenue intelligence will expand as transaction history grows.
          </div>

        </div>
      </div>

    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">

      <button type="button" onClick={() => setActiveTab("customers")} className="h-auto rounded-2xl border bg-white/85 p-4 text-left shadow-sm">
        <Users className="h-5 w-5 text-blue-600 mb-3" />
        <div className="font-semibold">Customer Management</div>
        <div className="text-xs text-muted-foreground mt-1">
          View accounts and activity
        </div>
      </button>

      <button type="button" onClick={() => setActiveTab("payments")} className="h-auto rounded-2xl border bg-white/85 p-4 text-left shadow-sm">
        <IndianRupee className="h-5 w-5 text-emerald-600 mb-3" />
        <div className="font-semibold">Payments & Payouts</div>
        <div className="text-xs text-muted-foreground mt-1">
          Monitor marketplace transactions
        </div>
      </button>

      <button type="button" onClick={() => setActiveTab("categories")} className="h-auto rounded-2xl border bg-white/85 p-4 text-left shadow-sm">
        <LayoutGrid className="h-5 w-5 text-violet-600 mb-3" />
        <div className="font-semibold">Manage Categories</div>
        <div className="text-xs text-muted-foreground mt-1">
          {stats.categories} active categories
        </div>
      </button>

      <button type="button" onClick={() => setActiveTab("banners")} className="h-auto rounded-2xl border bg-white/85 p-4 text-left shadow-sm">
        <Megaphone className="h-5 w-5 text-pink-600 mb-3" />
        <div className="font-semibold">Marketing & Ads</div>
        <div className="text-xs text-muted-foreground mt-1">
          {stats.banners} banners configured
        </div>
      </button>

    </div>

    <div className="rounded-3xl border border-white/70 bg-white/80 backdrop-blur-xl shadow-sm p-5 sm:p-6">

      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-display font-bold text-lg">
            Attention Required
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Items that may need administrator action
          </p>
        </div>

        <Clock className="h-5 w-5 text-amber-500" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">

        <button type="button" onClick={() => setActiveTab("kyc")} className="h-auto rounded-2xl border bg-amber-50/70 border-amber-100 p-4 text-left">
          <div className="text-xs text-amber-700">Pending KYC</div>
          <div className="text-2xl font-display font-extrabold mt-1">
            {stats.pending_vendors}
          </div>
        </button>

        <button type="button" onClick={() => setActiveTab("reviews")} className="h-auto rounded-2xl border bg-yellow-50/70 border-yellow-100 p-4 text-left">
          <div className="text-xs text-yellow-700">Pending Reviews</div>
          <div className="text-2xl font-display font-extrabold mt-1">
            {stats.pending_reviews}
          </div>
        </button>

        <button type="button" onClick={() => setActiveTab("bookings")} className="h-auto rounded-2xl border bg-orange-50/70 border-orange-100 p-4 text-left">
          <div className="text-xs text-orange-700">Refund Requests</div>
          <div className="text-2xl font-display font-extrabold mt-1">
            {stats.refund_requests}
          </div>
        </button>

        <button type="button" onClick={() => setActiveTab("vendors")} className="h-auto rounded-2xl border bg-emerald-50/70 border-emerald-100 p-4 text-left">
          <div className="text-xs text-emerald-700">Verified Vendors</div>
          <div className="text-2xl font-display font-extrabold mt-1">
            {stats.verified_vendors}
          </div>
        </button>

      </div>
    </div>

  </div>
</TabsContent>

<TabsContent value="kyc" className="pt-5"><VendorsTab vendors={vendors} reload={loadAll} queueOnly /></TabsContent>
          <TabsContent value="vendors" className="pt-5"><VendorsTab vendors={vendors} reload={loadAll} /></TabsContent>
          <TabsContent value="fields" className="pt-5"><FieldBuilderTab categories={categories} /></TabsContent>
          <TabsContent value="categories" className="pt-5"><CategoryManagerTab categories={allCats} reload={loadAll} /></TabsContent>
          <TabsContent value="locations" className="pt-5"><LocationsTab /></TabsContent>
          <TabsContent value="customers" className="pt-5"><CustomersTab /></TabsContent>
          <TabsContent value="employees" className="pt-5"><EmployeesTab /></TabsContent>
          <TabsContent value="bookings" className="pt-5"><BookingsTab /></TabsContent>
          <TabsContent value="payments" className="pt-5"><PaymentsTab /></TabsContent>
          <TabsContent value="billing" className="pt-5"><BillingTab /></TabsContent>
          <TabsContent value="paysettings" className="pt-5"><PaymentSettingsTab /></TabsContent>
          <TabsContent value="integrations" className="pt-5"><IntegrationsTab /></TabsContent>
          <TabsContent value="coupons" className="pt-5"><CouponsTab categories={categories} /></TabsContent>
          <TabsContent value="reviews" className="pt-5"><ReviewsTab /></TabsContent>
          <TabsContent value="banners" className="pt-5"><BannersTab categories={categories} /></TabsContent>
          <TabsContent value="plans" className="pt-5"><PlansTab /></TabsContent>
          <TabsContent value="homepage" className="pt-5"><HomepageManagerTab /></TabsContent>
          <TabsContent value="pages" className="pt-5"><PagesTab /></TabsContent>
          <TabsContent value="notify" className="pt-5"><NotifyTab /></TabsContent>
          <TabsContent value="settings" className="pt-5"><SettingsTab /></TabsContent>
          <TabsContent value="audit" className="pt-5"><AuditTab /></TabsContent>
    </main>
  </div>
</Tabs>
      </div>
    </div>
  );
}
